import React, { useState, useEffect } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../../../db/firebase";
import { AnimatedModal } from "../../../../components/AnimatedModal";
import { useTranslation } from "react-i18next";
import { HiOutlineXMark, HiOutlineEnvelope } from "react-icons/hi2";

interface InviteToServiceModalProps {
  eventId: string;
  onClose: () => void;
}

const InviteToServiceModal: React.FC<InviteToServiceModalProps> = ({
  eventId,
  onClose,
}) => {
  const { t } = useTranslation();
  const [assignedMembers, setAssignedMembers] = useState<any[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [eventDetails, setEventDetails] = useState<any | null>(null);

  useEffect(() => {
    const fetchEventAndMembers = async () => {
      try {
        const eventDoc = await getDoc(doc(db, "events", eventId));
        const eventData = eventDoc.data();
        if (eventData?.servingMembers?.length) {
          const memberIds = eventData.servingMembers;
          const q = query(
            collection(db, "users"),
            where("__name__", "in", memberIds.slice(0, 10))
          );
          const snapshot = await getDocs(q);
          const members = snapshot.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));
          setAssignedMembers(members);
          setEventDetails({ id: eventId, ...eventData });
        }
      } catch (err) {
        setError("Failed to fetch event and member details.");
      }
    };
    fetchEventAndMembers();
  }, [eventId]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || selectedMembers.length === 0 || !eventDetails) {
      if (selectedMembers.length === 0) setError("Please select at least one member.");
      return;
    }
    setIsSubmitting(true);
    setError("");
    try {
      const selectedEmails = assignedMembers
        .filter((m) => selectedMembers.includes(m.id))
        .map((m) => m.email);
      const response = await fetch("/.netlify/functions/sendEventNotification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emails: selectedEmails, eventDetails }),
      });
      if (response.ok) {
        setSuccess("Invitations sent successfully!");
        setSelectedMembers([]);
      } else {
        const data = await response.json();
        setError(data.error || "Failed to send invitations");
      }
    } catch (err) {
      setError("Failed to send invitations. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckboxChange = (memberId: string) => {
    setSelectedMembers((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) setSelectedMembers([]);
    else setSelectedMembers(assignedMembers.map((m) => m.id));
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    setSelectAll(
      selectedMembers.length === assignedMembers.length && assignedMembers.length > 0
    );
  }, [selectedMembers, assignedMembers]);

  return (
    <AnimatedModal isOpen onClose={onClose} backdropClassName="bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-lg rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800/80">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA]">
              <HiOutlineEnvelope className="w-5 h-5" strokeWidth={2} />
            </div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              Invite to Service
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={t("close")}
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 max-h-[60vh] overflow-y-auto">
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50">
              <input
                type="checkbox"
                id="select-all"
                checked={selectAll}
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-[#5932EA] focus:ring-[#5932EA]"
              />
              <label
                htmlFor="select-all"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Select All
              </label>
            </div>
            <ul className="space-y-2 max-h-48 overflow-y-auto">
              {assignedMembers.map((member) => (
                <li
                  key={member.id}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <input
                    type="checkbox"
                    id={`member-${member.id}`}
                    checked={selectedMembers.includes(member.id)}
                    onChange={() => handleCheckboxChange(member.id)}
                    className="rounded border-gray-300 text-[#5932EA] focus:ring-[#5932EA]"
                  />
                  <label
                    htmlFor={`member-${member.id}`}
                    className="text-gray-800 dark:text-gray-200 cursor-pointer flex-1"
                  >
                    {member.firstName} {member.lastName}
                  </label>
                </li>
              ))}
            </ul>
            {error && (
              <p className="text-red-500 dark:text-red-400 text-sm">{error}</p>
            )}
            {success && (
              <p className="text-emerald-600 dark:text-emerald-400 text-sm">
                {success}
              </p>
            )}
            <button
              type="submit"
              disabled={isSubmitting || selectedMembers.length === 0}
              className="w-full py-2.5 rounded-xl font-medium bg-[#5932EA] text-white hover:bg-[#4a28c7] disabled:opacity-50 transition-colors"
            >
              {isSubmitting ? t("sending") || "Sending..." : "Invite"}
            </button>
          </form>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default InviteToServiceModal;
